import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HvButton,
  HvDialog,
  HvDialogTitle,
  HvDialogContent,
  HvDialogActions,
  HvBanner,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import Loading from "components/layout/Loading";

import axios from "axios";
import useStyles from "./styles";

export interface ArchiveDeleteResponse<T extends string | number> {
  detail?: {
    succeed: T[];
    failed: T[];
  };
}
interface DialogProps<T extends ArchiveDeleteResponse<string | number>> {
  scope: string;
  isOpen: boolean;
  promise?: (id: string) => Promise<T>;
  promiseAll?: (id: string[]) => Promise<T>;
  onClose: (success: boolean, message?: string, ids?: string[]) => void;
  ids: string[];
}

const ArchiveDialog = <T extends ArchiveDeleteResponse<string | number>>(
  props: DialogProps<T>
): JSX.Element => {
  const { scope, isOpen, promise, promiseAll, onClose, ids } = props;
  const classes = useStyles();
  const [isArchiving, setIsArchiving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { t } = useTranslation(scope);

  const onCloseHandler = (
    event: unknown,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason !== "backdropClick") {
      onClose?.(false);
    }
  };

  const onCancelHandler = () => {
    onClose?.(false);
  };

  const onArchive = async () => {
    setIsArchiving(true);
    let fulfilledIds: string[] = [];
    const totalCount = ids.length;
    let rejectedCount = 0;
    let networkError = false;

    const archiveIdsArray = Object.values(ids);
    if (promise !== undefined && promise !== null) {
      const archivePromises: Promise<T>[] = ids.map((id) => promise(id));
      const results = await Promise.allSettled(archivePromises);

      results.forEach((result, index) => {
        const { status } = result;

        if (status === "fulfilled") {
          const { detail } = (result as PromiseFulfilledResult<T>).value as T;

          if (detail) {
            const { succeed, failed } = detail;
            if (succeed.length) {
              fulfilledIds.push(archiveIdsArray[index]);
            } else if (failed.length) {
              rejectedCount += 1;
            }
          } else {
            fulfilledIds.push(archiveIdsArray[index]);
          }
        } else {
          const { reason } = result as PromiseRejectedResult;
          if (reason.message === "Network Error") networkError = true;
          rejectedCount += 1;
        }
      });
    } else if (promiseAll !== undefined && promiseAll !== null) {
      try {
        const result = await promiseAll(ids);
        if (result.detail?.succeed as string[]) {
          fulfilledIds = fulfilledIds.concat(
            (result.detail?.succeed as string[]) ?? []
          );
        } else {
          fulfilledIds.concat((result.detail?.succeed as number[]).map(String));
        }
        rejectedCount += result.detail?.failed.length ?? 0;
      } catch (e) {
        if (axios.isAxiosError(e)) {
          if (e.message === "Network Error") {
            networkError = true;
          }
        }
        rejectedCount += ids.length;
      }
    } else {
      rejectedCount += ids.length;
    }
    setIsArchiving(false);
    if (totalCount === fulfilledIds.length) {
      setError(undefined);
      onClose?.(
        true,
        t("archiveDialog.result.success", { count: totalCount, totalCount }),
        fulfilledIds
      );
    } else if (totalCount === rejectedCount) {
      setError(
        networkError
          ? t("archiveDialog.result.networkError")
          : t("archiveDialog.result.error")
      );
      setTimeout(() => {
        setError(undefined);
      }, 5000);
    } else {
      setError(undefined);
      onClose?.(
        false,
        t("archiveDialog.result.warning", { totalCount, rejectedCount }),
        fulfilledIds
      );
    }
  };

  const onBannerClose = () => {
    setError(undefined);
  };

  return (
    <HvDialog
      buttonTitle={t("archiveDialog.buttons.close")}
      classes={{ paper: classes.paper }}
      open={isOpen}
      onClose={onCloseHandler}
    >
      <HvDialogTitle className={classes.dialogTitle}>
        {t("archiveDialog.title", { count: ids.length })}
      </HvDialogTitle>
      <HvDialogContent>
        <HvBanner
          className={classes.errorBanner}
          style={{ top: undefined }}
          open={error != null}
          variant="error"
          showIcon
          label={error}
          onClose={onBannerClose}
        >
          <HvTypography>{error}</HvTypography>
        </HvBanner>
        <HvTypography>
          {t("archiveDialog.description", { count: ids.length })}
        </HvTypography>
      </HvDialogContent>
      {isArchiving ? (
        <Loading
          delay={500}
          loadingLabel={t("archiveDialog.submitting", {
            count: ids.length,
          })}
          className={classes.loading}
        />
      ) : (
        <></>
      )}
      <HvDialogActions>
        <HvButton
          category="ghost"
          type="submit"
          disabled={isArchiving}
          onClick={onArchive}
        >
          {t("archiveDialog.buttons.archive")}
        </HvButton>
        <HvButton
          category="ghost"
          onClick={onCancelHandler}
          disabled={isArchiving}
        >
          {t("archiveDialog.buttons.cancel")}
        </HvButton>
      </HvDialogActions>
    </HvDialog>
  );
};

export default ArchiveDialog;
